import { SortOrder } from "./SortOrder";

export const getSortedCollection = <T>(items: T[], sortBy: string, sortOrder: SortOrder = "asc"): T[] => {
    const itemsWithUndefinedSortByProperty = items.filter((item: T) => item[sortBy] === undefined);
    const itemsToSort = items.filter((item: T) => item[sortBy] !== undefined);

    if (!itemsToSort || itemsToSort.length < 2) {
        return concatDefinedAndUndefinedPropertyItems(itemsToSort, itemsWithUndefinedSortByProperty, sortOrder);
    }

    const exampleObject = itemsToSort[0];

    if (typeof exampleObject[sortBy] === "string") {
        sortCollectionByStringCaseInsensitive<T>(itemsToSort, sortOrder, sortBy);

        return concatDefinedAndUndefinedPropertyItems(itemsToSort, itemsWithUndefinedSortByProperty, sortOrder);
    }

    if (typeof exampleObject[sortBy] === "number") {
        sortCollectionByNumberProperty<T>(itemsToSort, sortOrder, sortBy);

        return concatDefinedAndUndefinedPropertyItems(itemsToSort, itemsWithUndefinedSortByProperty, sortOrder);
    }

    if (typeof exampleObject[sortBy] === "boolean") {
        sortCollectionByBooleanProperty<T>(itemsToSort, sortOrder, sortBy);

        return concatDefinedAndUndefinedPropertyItems(itemsToSort, itemsWithUndefinedSortByProperty, sortOrder);
    }

    if (exampleObject[sortBy] instanceof Date) {
        sortCollectionByDateProperty<T>(itemsToSort, sortOrder, sortBy);

        return concatDefinedAndUndefinedPropertyItems(itemsToSort, itemsWithUndefinedSortByProperty, sortOrder);
    }

    throw new Error("Unsupported sorting type");
};

const concatDefinedAndUndefinedPropertyItems = <T>(
    definedPropertyItems: T[],
    undefinedPropertyItems: T[],
    sortOrder: SortOrder
): T[] => {
    if (sortOrder === "asc") {
        return undefinedPropertyItems.concat(definedPropertyItems);
    }

    return definedPropertyItems.concat(undefinedPropertyItems);
};

const naturalStringCompare = (a: string, b: string): number => {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
};

const sortCollectionByStringCaseInsensitive = <T>(collection: T[], order: SortOrder, property: string) => {
    if (order === "asc") {
        collection.sort((a: T, b: T) => naturalStringCompare(a[property] as string, b[property] as string));
    }

    if (order === "desc") {
        collection.sort((a: T, b: T) => naturalStringCompare(b[property] as string, a[property] as string));
    }
};

const sortCollectionByNumberProperty = <T>(collection: T[], order: SortOrder, property: string) => {
    if (order === "asc") {
        collection.sort((a: T, b: T) => ((a[property] as number) < (b[property] as number) ? -1 : 1));
    }

    if (order === "desc") {
        collection.sort((a: T, b: T) => ((a[property] as number) > (b[property] as number) ? -1 : 1));
    }
};

const sortCollectionByBooleanProperty = <T>(collection: T[], order: SortOrder, property: string) => {
    if (order === "asc") {
        collection.sort((a: T, b: T) => (a[property] < b[property] ? -1 : 1));
    }

    if (order === "desc") {
        collection.sort((a: T, b: T) => (a[property] > b[property] ? -1 : 1));
    }
};

const sortCollectionByDateProperty = <T>(collection: T[], order: SortOrder, property: string) => {
    if (order === "asc") {
        collection.sort((a: T, b: T) => ((a[property] as Date) < (b[property] as Date) ? -1 : 1));
    }

    if (order === "desc") {
        collection.sort((a: T, b: T) => ((a[property] as Date) > (b[property] as Date) ? -1 : 1));
    }
};

export const getSortedObjectCollection = <T>(
    collection: T[],
    order: SortOrder,
    itemToString: (item: T) => string
): T[] => {
    const itemToSort = collection.concat();

    if (order === "asc") {
        itemToSort.sort((a: T, b: T) => naturalStringCompare(itemToString(a), itemToString(b)));
    }

    if (order === "desc") {
        itemToSort.sort((a: T, b: T) => naturalStringCompare(itemToString(b), itemToString(a)));
    }

    return itemToSort;
};

export const getSortedCollectionByCustomRule = <T>(
    collection: T[],
    order: SortOrder,
    customRule: (item1: T, item2: T) => boolean
): T[] => {
    if (order === "asc") {
        collection.sort((a: T, b: T) => (customRule(a, b) ? -1 : 1));

        return collection;
    }

    collection.sort((a: T, b: T) => (!customRule(a, b) ? -1 : 1));

    return collection;
};
